import { useMemo } from 'react';
import { css } from '@understory-io/leaf/css';

/**
 * HTML that `renderMarkdownToHtml` produced. Branded so that `<MarkdownHtml>`,
 * which injects it unescaped, can't be handed any other string.
 */
export type RenderedMarkdown = string & {
  readonly __brand: 'RenderedMarkdown';
};

/**
 * Shows markdown that `renderMarkdownToHtml` rendered on the server, styled
 * exactly like `<Markdown>`. Lives apart from `markdown.tsx` so a page that
 * only shows pre-rendered markdown doesn't ship the markdown parser.
 */
export function MarkdownHtml({ html }: { html: RenderedMarkdown }) {
  // React 19 compares this prop by object identity, not by its `__html`, so a
  // fresh object would rewrite innerHTML on every re-render: the description's
  // nodes get replaced and a visitor's text selection in it is lost.
  const innerHtml = useMemo(() => ({ __html: html }), [html]);
  return (
    <div
      className={markdownStyles}
      // Only ever React's own render of `<Markdown>`: raw HTML in the source
      // comes out as escaped text and link targets pass `safeHref`, so this
      // string carries nothing a client render of the same markdown wouldn't.
      dangerouslySetInnerHTML={innerHtml}
    />
  );
}

export const markdownStyles = css({
  overflow: 'hidden',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  minWidth: 'none',

  '& h2': {
    marginBottom: 'tiny',
  },
  '& * + h2': {
    marginTop: 'large',
  },
  '& p': {
    margin: 'none',
  },
  '& p + p': {
    marginTop: 'small',
  },
  '& ul, & ol': {
    marginTop: 'small',
    marginBottom: 'medium',
  },
  '& ul': {
    listStyleType: 'disc',
  },
  '& ol': {
    listStyleType: 'decimal',
  },
});
